import { Component } from '@theme/component';
import { preloadImage } from '@theme/utilities';

/**
 * A self-contained, client-side variant picker for a product card.
 *
 * Unlike `swatches-variant-picker-component` (assets/product-card.js), this component does not
 * fetch `?section_id=section-rendering-product-card` on every interaction and does not dispatch the
 * theme's variant events. Everything it needs is embedded as JSON at render time, so option changes
 * are instant and stay scoped to a single card.
 *
 * Dispatching `VariantSelectedEvent` here would be actively harmful: `ProductFormComponent` listens
 * on `this.closest('.shopify-section, dialog, product-card')` (product-form.js:168) and its
 * `#onVariantSelected` handler (product-form.js:550) does not filter by product id, so the event
 * would disable the Add to cart button on every card in the section. Instead this component writes
 * straight to its own card's form.
 *
 * The host markup is addressed through generic `rr-card-*` hooks rather than any one section's
 * class names, so every section that opts in shares this component and one stylesheet:
 *
 *   .rr-card                                  the card root
 *     a.rr-card-link                          product link, gains ?variant= on selection
 *     img.rr-card-image                       featured image
 *     img.rr-card-image-hover                 optional hover image
 *     product-form-component > form > input[ref=variantId]
 *     .rr-card-price                          price text
 *     card-variants-component
 *       <script type="application/json"> … CardVariantsData … </script>
 *       fieldset > .rr-variants__selected[data-option-index]
 *               > label > input[type=radio][data-option-index][data-option-value]
 *
 * @typedef {object} CardOption
 * @property {string} name
 * @property {string[]} values - In the order Liquid rendered them.
 *
 * @typedef {object} CardVariant
 * @property {number} id
 * @property {string[]} opts - Option values, in option position order.
 * @property {boolean} available
 * @property {string} price - Pre-formatted with the shop's money format (may contain markup).
 * @property {string} [image] - Master image URL; omitted when it is the card's default image.
 * @property {string} [media] - Featured media id, used to drive a `card-gallery` slideshow.
 *
 * @typedef {object} CardVariantsData
 * @property {CardOption[]} options
 * @property {CardVariant[]} variants
 * @property {number[]} [hiddenOptions] - Positions the card did not render (see below).
 * @property {string} [defaultImage] - Master URL used by variants with no image of their own.
 * @property {string[]} [sharedImages] - Master URLs of images attached to no variant.
 * @property {{add: string, soldOut: string, unavailable: string}} text
 */

/** Widths emitted in `srcset`. The card is ~22vw on desktop, ~45vw on mobile. */
const IMAGE_WIDTHS = [400, 800];

class CardVariantsComponent extends Component {
  /** @type {CardOption[]} */
  #options = [];

  /** @type {CardVariant[]} */
  #variants = [];

  /** @type {string[]} */
  #selected = [];

  /**
   * Option positions the card chose not to render — the Size option when the section's "Hide size
   * selector" setting is on.
   *
   * A hidden option is still part of every variant, so the component keeps picking a value for it;
   * it just does so itself, always landing on a combination that exists and preferring one that is
   * in stock. It is also treated as unconstraining when working out which *visible* values are
   * still available, because the shopper has no way to change it.
   *
   * @type {Set<number>}
   */
  #hidden = new Set();

  /** @type {string | undefined} */
  #defaultImage;

  /** @type {string[]} */
  #sharedImages = [];

  /** @type {CardVariantsData['text']} */
  #text = { add: '', soldOut: '', unavailable: '' };

  connectedCallback() {
    super.connectedCallback();

    const raw = this.querySelector('script[type="application/json"]')?.textContent;
    if (!raw) return;

    /** @type {CardVariantsData} */
    let data;
    try {
      data = JSON.parse(raw);
    } catch (error) {
      console.error('card-variants: could not parse variant data', error);
      return;
    }

    this.#options = data.options ?? [];
    this.#variants = data.variants ?? [];
    if (!this.#options.length || !this.#variants.length) return;

    this.#hidden = new Set(
      (data.hiddenOptions ?? []).filter((index) => Number.isInteger(index) && index < this.#options.length)
    );

    this.#defaultImage = data.defaultImage;
    this.#sharedImages = data.sharedImages ?? [];
    this.#text = { add: '', soldOut: '', unavailable: '', ...data.text };
    this.#selected = this.#readSelection();

    this.addEventListener('change', this.#handleChange);

    // Reconcile the server-rendered state. Liquid can only mark option values with Shopify's
    // `product_option_value.available`, which is a global "some variant with this value is
    // available" flag rather than a combination-aware one, so the first paint has to be ours.
    this.#paint();
    this.#applyLabels();
    this.#applyPurchasability(this.#findVariant(this.#selected));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleChange);
  }

  /**
   * The card this picker belongs to.
   * @returns {HTMLElement | null}
   */
  get card() {
    return this.closest('.rr-card');
  }

  /**
   * All option radios.
   * @returns {HTMLInputElement[]}
   */
  get inputs() {
    return /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('input[data-option-index]')));
  }

  /**
   * Builds the current selection from whichever radios Liquid marked checked, then fills in the gaps
   * — a hidden option, or an option Liquid preselected nothing for.
   *
   * The gaps are filled per position rather than by replacing the whole tuple: with Size hidden, the
   * shopper's colour is still on the page as a checked radio, and swapping in an unrelated variant's
   * options wholesale would silently move it.
   *
   * @returns {string[]}
   */
  #readSelection() {
    /** @type {(string | null)[]} */
    const selection = new Array(this.#options.length).fill(null);

    for (const input of this.inputs) {
      if (!input.checked) continue;
      const index = Number(input.dataset.optionIndex);
      if (Number.isInteger(index)) selection[index] = input.dataset.optionValue ?? null;
    }

    if (selection.includes(null)) {
      const fallback =
        this.#bestVariantFor(selection) ?? this.#variants.find((variant) => variant.available) ?? this.#variants[0];

      for (let index = 0; index < selection.length; index++) {
        if (selection[index] != null) continue;
        selection[index] = fallback.opts[index] ?? this.#options[index].values[0] ?? null;
      }
    }

    return /** @type {string[]} */ (selection);
  }

  /**
   * The best variant matching a partial selection — `null` entries match anything.
   *
   * Prefers an in-stock variant, so hiding Size lands the shopper on a size they can actually buy
   * rather than on whichever one happens to come first.
   *
   * @param {(string | null)[]} partial
   * @returns {CardVariant | undefined}
   */
  #bestVariantFor(partial) {
    const matches = (/** @type {CardVariant} */ variant) =>
      partial.every((value, index) => value == null || variant.opts[index] === value);

    return this.#variants.find((variant) => variant.available && matches(variant)) ?? this.#variants.find(matches);
  }

  /**
   * Re-picks every hidden option from the variants that match what the shopper can actually see.
   *
   * `#select`'s repair loop cannot do this: it only walks forward from the option that changed, and
   * a hidden option may sit before the visible ones (Shopify orders options however the merchant
   * set them up, so Size is often position 1).
   */
  #resolveHidden() {
    if (!this.#hidden.size) return;

    const visibleOnly = this.#selected.map((value, index) => (this.#hidden.has(index) ? null : value));
    const match = this.#bestVariantFor(visibleOnly);
    if (!match) return;

    for (const index of this.#hidden) {
      if (match.opts[index] != null) this.#selected[index] = match.opts[index];
    }
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.dataset.optionIndex == null) return;

    const index = Number(input.dataset.optionIndex);
    const value = input.dataset.optionValue;
    if (!Number.isInteger(index) || value == null) return;

    this.#select(index, value);

    const variant = this.#findVariant(this.#selected);

    this.#syncCheckedInputs();
    this.#paint();
    this.#applyLabels();
    this.#applyPurchasability(variant);
    this.#applyImage(variant);
    this.#applyPrice(variant);
    this.#applyLink(variant);
  };

  /**
   * Writes the chosen value into each option's label, so the card reads "Color: Espresso" the same
   * way the product page does.
   */
  #applyLabels() {
    for (const output of this.querySelectorAll('.rr-variants__selected')) {
      const index = Number(output.getAttribute('data-option-index'));
      if (!Number.isInteger(index)) continue;

      output.textContent = this.#selected[index] ?? '';
    }
  }

  /**
   * Applies a choice, then repairs the options *after* it so the selection lands on a variant that
   * actually exists — preferring an available one.
   *
   * Picking a colour that simply is not made in 4XL pulls Size back to a size that colour does come
   * in, rather than leaving an impossible tuple. The choice the shopper made is never moved.
   *
   * @param {number} index
   * @param {string} value
   */
  #select(index, value) {
    this.#selected[index] = value;

    for (let position = index + 1; position < this.#options.length; position++) {
      // Hidden options are repaired by #resolveHidden below, which can see the whole selection
      // rather than only the options ahead of the one that changed.
      if (this.#hidden.has(position)) continue;
      if (this.#stateFor(position, this.#selected[position]).available) continue;

      const values = this.#options[position].values;

      this.#selected[position] =
        values.find((candidate) => this.#stateFor(position, candidate).available) ??
        values.find((candidate) => this.#stateFor(position, candidate).exists) ??
        this.#selected[position];
    }

    this.#resolveHidden();
  }

  /**
   * Whether an option value exists and/or is available, given the current selection.
   *
   * Only the options *before* `index` constrain the answer — the cascade model Shopify's own
   * `?option_values=` rendering and the product page both use. The asymmetry is deliberate: if later
   * options could grey out earlier ones, a sparse catalogue can mark every value of a middle option
   * unavailable and leave the shopper with no way out.
   *
   * @param {number} index
   * @param {string} value
   * @returns {{exists: boolean, available: boolean}}
   */
  #stateFor(index, value) {
    let exists = false;

    for (const variant of this.#variants) {
      if (variant.opts[index] !== value) continue;

      let matchesPrefix = true;
      for (let position = 0; position < index; position++) {
        // A hidden option never narrows a visible one. The shopper cannot change it, so letting it
        // constrain the answer would grey out a colour that is perfectly buyable in another size —
        // and leave no control on the card to recover with.
        if (this.#hidden.has(position)) continue;
        if (variant.opts[position] !== this.#selected[position]) {
          matchesPrefix = false;
          break;
        }
      }
      if (!matchesPrefix) continue;

      exists = true;
      if (variant.available) return { exists: true, available: true };
    }

    return { exists, available: false };
  }

  /**
   * The variant matching a complete selection.
   *
   * A linear scan rather than a joined-string lookup key: option values can contain any character,
   * so any separator risks a collision, and the lists here are at most a few dozen entries.
   *
   * The comparison walks `selection`, not `opts`, so a `variant.options` array padded past the
   * product's option count does not fail every match.
   *
   * @param {string[]} selection
   * @returns {CardVariant | undefined}
   */
  #findVariant(selection) {
    return this.#variants.find((variant) => selection.every((value, index) => variant.opts[index] === value));
  }

  /**
   * Checks the radios matching the current selection, in case `#select` moved an option the shopper
   * did not click.
   */
  #syncCheckedInputs() {
    for (const input of this.inputs) {
      const index = Number(input.dataset.optionIndex);
      input.checked = input.dataset.optionValue === this.#selected[index];
    }
  }

  /**
   * Marks each option value available, sold out, or nonexistent.
   *
   * Inputs are deliberately never `disabled` — that would stop the shopper re-selecting them, and
   * `#select`'s repair step is what rescues an impossible combination. State is carried on
   * `aria-disabled` / `data-option-available` / `data-option-exists` and expressed in CSS, matching
   * how snippets/variant-main-picker.liquid does it on the product page.
   */
  #paint() {
    for (const input of this.inputs) {
      const index = Number(input.dataset.optionIndex);
      const value = input.dataset.optionValue;
      if (!Number.isInteger(index) || value == null) continue;

      const { exists, available } = this.#stateFor(index, value);

      input.dataset.optionAvailable = String(available);
      input.dataset.optionExists = String(exists);

      if (available) {
        input.removeAttribute('aria-disabled');
      } else {
        input.setAttribute('aria-disabled', 'true');
      }
    }
  }

  /**
   * Points the card's product form at the selected variant and updates the button.
   *
   * The button's `disabled` is set directly rather than through `AddToCartComponent.disable()`, so
   * this works even if `add-to-cart-component` has not upgraded yet.
   *
   * @param {CardVariant | undefined} variant
   */
  #applyPurchasability(variant) {
    const card = this.card;
    if (!card) return;

    const variantInput = card.querySelector('input[ref="variantId"]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = variant ? String(variant.id) : '';

    const purchasable = Boolean(variant?.available);

    const button = card.querySelector('button[ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement) button.disabled = !purchasable;

    const label = card.querySelector('.add-to-cart-text__content');
    if (label) {
      label.textContent = purchasable ? this.#text.add : variant ? this.#text.soldOut : this.#text.unavailable;
    }

    // Keeps the fly-to-cart animation showing the variant the shopper actually chose.
    const media = this.#imageFor(variant);
    if (media) {
      card.querySelector('add-to-cart-component')?.setAttribute('data-product-variant-media', this.#sized(media, 100));
    }
  }

  /**
   * Swaps the card's featured image.
   *
   * `srcset` is reassigned alongside `src` on purpose — with a `srcset` present the browser ignores
   * a bare `src` change, so swapping only `src` would silently do nothing. The image is preloaded
   * first because `.product-image-rr img` carries `transition: opacity .4s`, and swapping to an
   * undecoded image flashes.
   *
   * `width`/`height` are left alone: they hold the card's aspect ratio, and `object-fit: cover`
   * crops any variant image shaped differently. Rewriting them would make the carousel jump.
   *
   * @param {CardVariant | undefined} variant
   */
  #applyImage(variant) {
    const card = this.card;
    if (!card) return;

    const primary = card.querySelector('.rr-card-image');

    // The theme's own collection card has no plain <img> to swap — its gallery is a slideshow whose
    // slides are keyed by media id (snippets/card-gallery.liquid). Drive that instead.
    if (!(primary instanceof HTMLImageElement)) {
      this.#selectGallerySlide(card, variant);
      return;
    }

    const image = this.#imageFor(variant);
    if (!image) return;

    const hover = card.querySelector('.rr-card-image-hover');

    // Images attached to no variant are the shop's shared lifestyle shots — the same assumption
    // blocks/_product-media-gallery.liquid makes for its `hide_variants` setting. Anything else in
    // `product.images` belongs to a specific variant, so showing it on hover would put another
    // colour's photo on this card. When there is no shared shot, hover matches primary and the
    // cross-fade simply becomes a no-op.
    const companion = this.#sharedImages.find((candidate) => candidate !== image) ?? image;

    preloadImage(this.#sized(image, IMAGE_WIDTHS[0]));

    if (primary instanceof HTMLImageElement) {
      primary.srcset = this.#srcset(image);
      primary.src = this.#sized(image, IMAGE_WIDTHS[0]);
    }

    if (hover instanceof HTMLImageElement) {
      hover.srcset = this.#srcset(companion);
      hover.src = this.#sized(companion, IMAGE_WIDTHS[0]);
    }
  }

  /**
   * Shows the selected variant's media in a `card-gallery` slideshow.
   *
   * Mirrors what the theme's own `ProductCard.#updateVariantImages` does (assets/product-card.js):
   * hide the variant-specific slides that belong to other variants, then select this one by media
   * id. Slides carry `slide-id` and, when they belong to a variant, a `variant-image` attribute.
   *
   * @param {HTMLElement} card
   * @param {CardVariant | undefined} variant
   */
  #selectGallerySlide(card, variant) {
    // Coerced to a string: Liquid emits the media id as a number, but `slide-id` is read back off
    // the DOM as a string, and a number would never match.
    const mediaId = variant?.media == null ? null : String(variant.media);
    if (!mediaId) return;

    const slideshow = /** @type {any} */ (card.querySelector('slideshow-component'));
    if (typeof slideshow?.select !== 'function') return;

    for (const slide of slideshow.refs?.slides ?? []) {
      if (slide.getAttribute('variant-image') == null) continue;
      slide.hidden = slide.getAttribute('slide-id') !== mediaId;
    }

    slideshow.select({ id: mediaId }, undefined, { animate: false });
  }

  /**
   * @param {CardVariant | undefined} variant
   * @returns {string | undefined}
   */
  #imageFor(variant) {
    return variant?.image ?? this.#defaultImage;
  }

  /**
   * Adds a width to a master image URL. Shopify's `image_url` always emits a `?v=` query string,
   * but the check keeps this correct if that ever changes.
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  #sized(url, width) {
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #srcset(url) {
    return IMAGE_WIDTHS.map((width) => `${this.#sized(url, width)} ${width}w`).join(', ');
  }

  /**
   * @param {CardVariant | undefined} variant
   */
  #applyPrice(variant) {
    // Falls back to the theme's own price element, so the block-composed collection card works
    // without having to edit blocks/price.liquid.
    const price =
      this.card?.querySelector('.rr-card-price') ?? this.card?.querySelector('product-price [ref="priceContainer"]');

    // A shop's `money_format` can contain markup, and this string came from our own Liquid.
    if (price && variant) price.innerHTML = variant.price;
  }

  /**
   * Carries the selection through to the product page.
   * @param {CardVariant | undefined} variant
   */
  #applyLink(variant) {
    const link =
      this.card?.querySelector('.rr-card-link') ?? this.card?.querySelector('a[ref="productCardLink"]');
    if (!(link instanceof HTMLAnchorElement) || !variant) return;

    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('variant', String(variant.id));
    link.href = url.toString();
  }
}

if (!customElements.get('card-variants-component')) {
  customElements.define('card-variants-component', CardVariantsComponent);
}
